import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { FaStar, FaCheckCircle, FaTimesCircle, FaArrowRight } from 'react-icons/fa';

const Comparison: React.FC = () => {
  const casinos = [
    {
      id: 'circusbet',
      name: 'CircusBet',
      bonus: '100€ de bonus',
      rating: 4.8,
      minDeposit: '10€',
      wagering: '35x',
      withdrawal: '24-48h',
      features: ['Retraits rapides', 'Paris en direct', 'Application mobile'],
      url: 'https://www.circusbet.fr/',
    },
    {
      id: 'genybet',
      name: 'GenyBet',
      bonus: '200€ de bonus',
      rating: 4.9,
      minDeposit: '20€',
      wagering: '30x',
      withdrawal: '12-24h',
      features: ['Programme VIP', 'Support 24/7', 'Cotes exclusives'],
      url: 'https://www.genybet.fr/',
    },
    {
      id: 'netbet',
      name: 'NetBet',
      bonus: '150€ de bonus',
      rating: 4.7,
      minDeposit: '10€',
      wagering: '40x',
      withdrawal: '24-72h',
      features: ['Paris sportifs', 'Cashback', 'Tournois'],
      url: 'https://www.netbet.fr/',
    },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Table',
    name: 'Comparaison des Bookmakers Français',
    description: 'Comparez les bonus, caractéristiques et conditions des bookmakers français côte à côte',
    url: 'https://parisportfrance.com/comparison',
  };

  return (
    <>
      <SEO
        title="Comparer les Bookmakers Français"
        description="Comparaison côte à côte des meilleurs bookmakers français. Comparez les bonus, conditions de mise, temps de retrait et caractéristiques."
        url="/comparison"
        schema={schema}
      />

      <div className="pt-24 md:pt-32 pb-20 min-h-screen bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Comparer les Bookmakers</h1>
            <p className="text-gray-400 text-lg">
              Comparez les bonus, caractéristiques et conditions pour trouver votre bookmaker parfait
            </p>
          </div>

          <div className="overflow-x-auto">
            <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-dark-surface border-b border-dark-border">
                    <th className="px-6 py-4 text-left text-white font-semibold">Bookmaker</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Bonus</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Note</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Dépôt min</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Conditions</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Retrait</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {casinos.map((casino, index) => (
                    <tr
                      key={casino.id}
                      className={`border-b border-dark-border ${
                        index % 2 === 0 ? 'bg-dark-card' : 'bg-dark-surface'
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="font-bold text-white">{casino.name}</div>
                        <div className="text-sm text-gray-400 mt-1">
                          {casino.features.join(', ')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-yellow-400 font-semibold">{casino.bonus}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          <FaStar className="text-yellow-400" />
                          <span className="text-white font-semibold">{casino.rating}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">{casino.minDeposit}</td>
                      <td className="px-6 py-4 text-gray-300">{casino.wagering}</td>
                      <td className="px-6 py-4 text-gray-300">{casino.withdrawal}</td>
                      <td className="px-6 py-4">
                        {casino.url ? (
                          <a
                            href={casino.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                          >
                            <span>Voir</span>
                            <FaArrowRight />
                          </a>
                        ) : (
                          <Link
                            to={`/offers/${casino.id}`}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                          >
                            <span>Voir</span>
                            <FaArrowRight />
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-12 bg-dark-card border border-dark-border rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Conseils de Comparaison</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <FaCheckCircle className="text-green-400 mr-2" />
                  Ce qu'il faut rechercher
                </h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Conditions de mise plus faibles (30x ou moins)</li>
                  <li>• Temps de retrait rapides</li>
                  <li>• Licence ARJEL</li>
                  <li>• Bonnes notes de support client</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <FaTimesCircle className="text-red-400 mr-2" />
                  Signaux d'alarme
                </h3>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Conditions de mise supérieures à 50x</li>
                  <li>• Dépôts minimum très élevés</li>
                  <li>• Processus de retrait lents</li>
                  <li>• Conditions générales peu claires</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Comparison;

